import React from 'react';
import * as Headless from '@headlessui/react';
import {
  ArrowUpTrayIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  InboxArrowDownIcon,
  LinkIcon,
  PaperClipIcon,
  RectangleStackIcon,
  XMarkIcon
} from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { AnimatePresence } from 'motion/react';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@oysterjs/ui/dialog';
import { Description, ErrorMessage, Field, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Link } from '@oysterjs/ui/link';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  type CaraWorkspaceArtifactEmailInput,
  type CaraWorkspaceFieldsFragment,
  CaraWorkspaceState,
  OutboundEmailStatus,
  type ProducerInformationFieldsFragment
} from '~/api/graphql';
import { ChooseClientDialog } from '~/components/ams-email-log/choose-client-dialog';
import { type PushableEmail, PushEmailToAms } from '~/components/ams-email-log/push-email-to-ams';
import { isEmailShaped, RecipientInput, type Recipient } from '~/components/recipient-input';
import { useCurrentAgencyUserId } from '~/components/current-user';
import { emailDraftMarkerExtensions } from '~/components/email-templates/draft-markers';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { useAmsLoggingEnabled } from '~/components/use-ams-logging-enabled';
import { CancelScheduleDialog, ScheduleSendDialog } from '~/components/schedule-send-dialog';
import { SignaturePreview } from '~/components/signature-preview';
import { draftsLocationName, mailClientName } from '~/lib/mailbox-providers';
import { formatSendTime, scheduledByAnotherUserMessage } from '~/lib/scheduled-send';

import {
  type AttachableArtifact,
  type AttachmentFile,
  type MailboxStatus,
  useEmailComposer
} from '~/components/workspaces/hooks/use-email-composer';
import { useAttachmentGroupMerge } from '~/components/workspaces/hooks/use-attachment-group-merge';
import {
  amsClientLoggedTo,
  useEmailAmsMatch
} from '~/components/workspaces/hooks/use-email-ams-match';
import { useSentEmailAmsLog } from '~/components/workspaces/hooks/use-sent-email-ams-log';
import { useComposerTemplateState } from '~/components/workspaces/hooks/use-composer-template-state';
import {
  type EmailTemplateSession,
  WAIT_FOR_CARA_REASON
} from '~/components/workspaces/hooks/use-template-redraft';
import { useFileDrop } from '~/components/cara-input/use-file-drop';
import { useRecipientSearch } from '~/components/workspaces/hooks/use-recipient-search';
import { getArtifactTitle } from '~/components/workspaces/lib/artifact';
import {
  type AttachmentGroup,
  groupContents,
  groupSummary,
  isPdfOnlyGroup
} from '~/components/workspaces/lib/attachment-group';
import {
  attachmentRefusal,
  bodyRefusal,
  formatFileSize
} from '~/components/cara-input/cara-attachment';
import { ArtifactFocusShell } from './artifact-focus-shell';
import { EmailAmsLine } from './artifact-focus-email-ams-line';
import { SentEmailAmsResult, SentEmailAmsUnknown } from './artifact-focus-email-ams-result';
import { EmailSendButton } from './artifact-focus-email-send-button';
import { templateHighlightClassName } from './artifact-focus-email-highlight';
import { EmailTemplatePicker } from './artifact-focus-email-template-picker';
import { EmailTemplateNotices } from './artifact-focus-email-template-notices';
import { EmailTemplateRowContent } from './artifact-focus-email-template-row';
import { ScheduledEmailActions, ScheduledEmailStatus } from './artifact-focus-email-scheduled';
import { AttachmentPickerDialog } from './attachment-picker-dialog';
import { HtmlDocumentEditor } from '~/pages/copilot/workspace/legacy-editor/html-document-editor';
import {
  type SaveStatus,
  SaveStatusIndicator
} from '~/components/document-editor/artifact-focus-html-document-save-status-indicator';
import { type FocusedArtifact } from '~/components/workspaces/lib/email-artifact-body';

type Artifact = FocusedArtifact;

/** Chip label falls back to the address when the sender never named the contact. */
const toRecipients = (
  recipients: readonly { name: string; email: string }[] | null | undefined
): Recipient[] =>
  (recipients ?? []).map((recipient) => ({
    email: recipient.email,
    ...(recipient.name ? { name: recipient.name } : {})
  }));

const toRecipientInput = (recipients: Recipient[]) =>
  recipients.map((recipient) => ({ email: recipient.email, name: recipient.name ?? '' }));

const sameText = (left: string, right: string) => left === right;

/** Order counts: it is the order the recipients will appear on the message. */
const sameRecipients = (left: Recipient[], right: Recipient[]) =>
  left.length === right.length &&
  left.every(
    (recipient, index) =>
      recipient.email === right[index].email && (recipient.name ?? '') === (right[index].name ?? '')
  );

const sameIds = (left: string[], right: string[]) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

/** The header rows as the artifact carries them, empty where it carries nothing. */
const rowsOf = (email: Artifact['email']) => ({
  subject: email?.subject ?? '',
  to: toRecipients(email?.to),
  cc: toRecipients(email?.cc),
  bcc: toRecipients(email?.bcc),
  signatureId: email?.signatureId ?? '',
  mailboxAccountId: email?.mailboxAccountId ?? '',
  attachmentArtifactIds: email?.attachmentArtifactIds ?? [],
  attachmentFileIds: email?.attachmentFileIds ?? []
});

/**
 * Moves one composer row onto the value the artifact now carries, unless the
 * row was already the sender's or the artifact never moved it. See the
 * reconcile in EmailFocus for what the two guards are between.
 */
const adoptRow = <T,>(
  claimed: boolean,
  shown: T,
  incoming: T,
  same: (left: T, right: T) => boolean,
  set: (value: T) => void
) => {
  if (claimed || same(shown, incoming)) return;
  set(incoming);
};

/**
 * `workspace.artifacts` carries every version, and an attachment is held by
 * artifact ID, so offering the same document once per version is meaningless.
 */
const latestByArtifactId = (artifacts: Artifact[]): Artifact[] => {
  const latest = new Map<string, Artifact>();
  artifacts.forEach((artifact) => {
    const seen = latest.get(artifact.id);
    if (!seen || artifact.version > seen.version) {
      latest.set(artifact.id, artifact);
    }
  });
  return [...latest.values()];
};

/** Why the send button is off, or undefined when it is armed. */
export const sendDisabledReason = (state: {
  loading: boolean;
  loadingMailboxDraft?: boolean;
  mailboxActionPending?: boolean;
  changingSender?: boolean;
  mailboxDraftLoadFailed?: boolean;
  bodyTooLarge?: boolean;
  sendableAccountCount: number;
  reconnectAccountCount?: number;
  selectedAccountNeedsReconnect?: boolean;
  to: Recipient[];
  cc: Recipient[];
  bcc: Recipient[];
  uploadingCount?: number;
  unresolvedAttachmentCount?: number;
  checkingAttachments?: boolean;
  unavailableArtifactCount?: number;
}): string | undefined => {
  if (state.loadingMailboxDraft) {
    return 'Checking mailbox status…';
  }
  if (state.mailboxActionPending) {
    return 'Finishing the mailbox action…';
  }
  if (state.changingSender) {
    return 'Finish changing the sender before sending or saving to a mailbox.';
  }
  if (state.mailboxDraftLoadFailed) {
    return 'Retry the mailbox status check before continuing.';
  }
  if (state.loading) {
    return 'Loading your connected mailboxes…';
  }
  // Before every other reason: an over-limit body is never written, so the
  // stored draft is not the email on screen.
  if (state.bodyTooLarge) {
    return 'The email body is over the size limit.';
  }
  if (state.uploadingCount) {
    return state.uploadingCount === 1
      ? 'Waiting for the attachment to finish uploading…'
      : 'Waiting for the attachments to finish uploading…';
  }
  if (state.checkingAttachments) {
    return 'Checking what this email carries…';
  }
  if (state.unresolvedAttachmentCount) {
    return state.unresolvedAttachmentCount === 1
      ? 'One attachment is no longer in this workspace. Remove it before sending.'
      : 'Some attachments are no longer in this workspace. Remove them before sending.';
  }
  if (state.unavailableArtifactCount) {
    return state.unavailableArtifactCount === 1
      ? 'One attached document is no longer available to you. Remove it before sending.'
      : 'Some attached documents are no longer available to you. Remove them before sending.';
  }
  if (state.selectedAccountNeedsReconnect) {
    return 'Reconnect your mailbox in Settings before sending.';
  }
  if (state.sendableAccountCount === 0) {
    // Telling someone who already connected a mailbox to connect one sends
    // them looking for a button they already pressed.
    return state.reconnectAccountCount
      ? 'Reconnect your mailbox in Settings before sending.'
      : 'Connect a mailbox in Settings before sending.';
  }
  if (state.to.length === 0) {
    return 'Add at least one recipient.';
  }
  if ([...state.to, ...state.cc, ...state.bcc].some((r) => !isEmailShaped(r.email))) {
    return 'One of the addresses does not look like an email address.';
  }
  return undefined;
};

/**
 * Where an attached document came from, or undefined when it came from the
 * workspace on screen. Both halves are optional on the server, so a document
 * with neither yields undefined rather than a label made of separators.
 */
const artifactOrigin = (
  artifact: AttachableArtifact,
  currentWorkspaceId: string
): string | undefined => {
  if (artifact.workspaceId === currentWorkspaceId) {
    return undefined;
  }
  return [artifact.clientName, artifact.workspaceTitle].filter(Boolean).join(' · ') || undefined;
};

/**
 * Pointer events stay off so the drag keeps landing on the zone underneath:
 * this is an affordance, not a target.
 */
const AttachmentDropOverlay = () => (
  <div
    className={clsx(
      'pointer-events-none absolute inset-0 z-10 flex items-center justify-center',
      'rounded-lg border border-dashed border-neutral-400 dark:border-neutral-600 bg-neutral-50/95 dark:bg-neutral-900/95'
    )}
  >
    <div className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
      <ArrowUpTrayIcon className="size-4" />
      Drop to attach to this email
    </div>
  </div>
);

/**
 * The chosen signature as the recipient will meet it, at the foot of the letter
 * under a broken rule. Framed rather than injected into the composer's DOM, and
 * rendered exactly as stored — see SignaturePreview for why both matter.
 */
const SignatureFooter = (props: {
  name: string;
  bodyHtml: string;
  /** False where Cara cannot send, and so appends this to nothing. */
  appendedOnSend: boolean;
}) => (
  <div className="mt-3 shrink-0 border-t border-dashed border-neutral-300 dark:border-neutral-600 pt-2">
    <p className="select-none text-xs text-neutral-400 dark:text-neutral-500">
      {props.appendedOnSend
        ? 'Email signature preview — added on send, not editable here.'
        : 'Email signature preview — not editable here.'}
    </p>
    {/* Sized for a column shared with the message: a signature here is checked
        at a glance, not read. */}
    <SignaturePreview
      title={`Preview of the ${props.name} signature`}
      bodyHtml={props.bodyHtml}
      maxHeight={160}
      className="mt-1"
    />
  </div>
);

/**
 * A control with no box of its own: the row's hairline and caret carry the
 * affordance a border would.
 */
const BARE_CONTROL = clsx(
  'w-full min-w-0 appearance-none border-0 bg-transparent p-0',
  'text-sm/6 text-neutral-950 placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
  'focus:outline-none focus:ring-0 dark:text-white'
);

/**
 * A bare control that is a `<select>`. `bg-none` is the whole difference:
 * @tailwindcss/forms paints a chevron into every select as a background image,
 * which `appearance-none` does not touch, so a row that draws its own caret
 * ends up with two. The caret here is a themed icon that dark mode follows and
 * the plugin's grey SVG is not; `pr-5` is the room it sits in.
 */
const BARE_SELECT = clsx(BARE_CONTROL, 'truncate bg-none pr-5');

/**
 * One header row: a quiet label inline at the start, the control beside it, and
 * a hairline underneath. The label is dimmed through a descendant variant
 * rather than a class on `Label`, which loses a specificity tie with the kit's
 * own colour.
 */
const ComposerRow = (props: {
  label: string;
  trailing?: React.ReactNode;
  /** An error the sender must answer, or a link they must be able to click. */
  description?: React.ReactNode;
  /** Off for the row that already has a rule beneath it. */
  divider?: boolean;
  children: React.ReactNode;
}) => (
  <Field
    className={clsx(
      'py-1.5 [&_[data-slot=label]]:text-neutral-500 dark:[&_[data-slot=label]]:text-neutral-400',
      props.divider !== false &&
        'border-b border-neutral-200 dark:border-neutral-700 focus-within:border-neutral-400 dark:focus-within:border-white/20'
    )}
  >
    <div className="flex items-start gap-2">
      <Label className="w-32 shrink-0 whitespace-nowrap">{props.label}</Label>
      <div className="min-w-0 flex-1">{props.children}</div>
      {props.trailing}
    </div>
    {props.description}
  </Field>
);

/**
 * The email composer: an EMAIL artifact edited in the artifact-focus rail.
 * Field edits write back as they happen so the draft survives a closed rail,
 * and the send re-saves first so the version the server pins is this one — see
 * `send` in use-email-composer.
 */
export const EmailFocus = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  artifact: Artifact;
  /** Producer signatures offered when a document is combined into a PDF attachment. */
  agencyProducerInformations?: ProducerInformationFieldsFragment[];
  userProducerInformations?: ProducerInformationFieldsFragment[];
  onClose: () => void;
  bodyHydrating?: boolean;
  templateSession: EmailTemplateSession;
  /**
   * Set for one email of a bulk draft: the address of the page's Send from, shown as a
   * read-only From. The bulk draft page sends every email, so there's no Send, Schedule or
   * Save to the mailbox here, and the template can't be changed.
   */
  bulkSendFromAddress?: string;
  /** Whether the reader may change the workspace. Without it, the AMS line names no actions. */
  canUpdate?: boolean;
}) => {
  const { artifact, workspace, bodyHydrating } = props;
  const isBulkEmail = props.bulkSendFromAddress !== undefined;
  const email = artifact.email;
  const scheduledSendFlagEnabled = useFlag(FEATURE_FLAGS.MAILBOX_SCHEDULED_SEND);

  const composer = useEmailComposer({
    workspaceId: workspace.id,
    workspaceState: workspace.state,
    artifactId: artifact.id,
    version: artifact.version,
    contentRevision: email?.contentRevision ?? '',
    bodyHydrating
  });

  const searchRecipients = useRecipientSearch();
  // Everything that puts this email in a real mailbox -- the from-address, the
  // draft and send buttons, the sender-change confirmation. What is left is the
  // composition itself, signature included, which the agent still reads and
  // copies out.
  const mailboxFlagEnabled = useFlag(FEATURE_FLAGS.MAILBOX);
  const currentAgencyUserId = useCurrentAgencyUserId();

  const [subject, setSubject] = React.useState(email?.subject ?? '');
  const [to, setTo] = React.useState<Recipient[]>(toRecipients(email?.to));
  const [cc, setCc] = React.useState<Recipient[]>(toRecipients(email?.cc));
  const [bcc, setBcc] = React.useState<Recipient[]>(toRecipients(email?.bcc));
  const [signatureId, setSignatureId] = React.useState(email?.signatureId ?? '');
  const [mailboxAccountId, setMailboxAccountId] = React.useState(email?.mailboxAccountId ?? '');
  const [pendingMailboxAccountId, setPendingMailboxAccountId] = React.useState<string | null>(null);
  const [unsendableMailboxError, setUnsendableMailboxError] = React.useState<string | null>(null);
  const [switchingMailbox, setSwitchingMailbox] = React.useState(false);
  const [mailboxActionPending, setMailboxActionPending] = React.useState(false);
  const mailboxAction = React.useRef(false);
  const [attachmentArtifactIds, setAttachmentArtifactIds] = React.useState<string[]>(
    email?.attachmentArtifactIds ?? []
  );
  const [attachmentFileIds, setAttachmentFileIds] = React.useState<string[]>(
    email?.attachmentFileIds ?? []
  );
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const [attachmentError, setAttachmentError] = React.useState<string | null>(null);
  const [scheduleDialog, setScheduleDialog] = React.useState<'schedule' | 'reschedule' | null>(
    null
  );
  const [cancelScheduleOpen, setCancelScheduleOpen] = React.useState(false);
  const [isCancelScheduleRefused, setIsCancelScheduleRefused] = React.useState(false);
  const [editingScheduled, setEditingScheduled] = React.useState(false);
  /**
   * Held apart from `validationError`: it is set by an edit and cleared by the
   * edit that undoes it, and must survive the clicks that reset the others.
   */
  const [bodyError, setBodyError] = React.useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  /**
   * Bumped on every opening and used as the picker's key, so an opening never
   * reuses an instance still leaving its close transition; reopening one
   * mid-leave can end with no dialog at all.
   */
  const [pickerOpening, setPickerOpening] = React.useState(0);
  /**
   * The combined PDFs on this draft. The draft has nowhere to record what a
   * combined PDF was made of: the email carries the merged file's id like any
   * other upload.
   */
  const [attachmentGroups, setAttachmentGroups] = React.useState<AttachmentGroup[]>([]);
  const [editingGroupFileId, setEditingGroupFileId] = React.useState<string | null>(null);
  /**
   * What the open workspace cannot say about an attached document. The draft
   * stores only IDs, so a document attached from another workspace has no name
   * here until one is carried in.
   */
  const [attachedArtifactDetails, setAttachedArtifactDetails] = React.useState<
    AttachableArtifact[]
  >([]);
  const [checkingAttachments, setCheckingAttachments] = React.useState(false);

  // Content reveals the rows regardless of the toggle — a draft Cara addressed
  // to someone must never carry a recipient the sender cannot see — and once
  // open they stay open.
  const [ccBccShown, setCcBccShown] = React.useState(
    (email?.cc?.length ?? 0) > 0 || (email?.bcc?.length ?? 0) > 0
  );
  const [ccBccFocus, setCcBccFocus] = React.useState(false);
  React.useEffect(() => {
    if (cc.length > 0 || bcc.length > 0) {
      setCcBccShown(true);
    }
  }, [cc.length, bcc.length]);

  // A sent email's line comes from its send record, not from matching.
  const isAmsLoggingEnabled = useAmsLoggingEnabled();
  const amsMatch = useEmailAmsMatch({
    enabled: isAmsLoggingEnabled && composer.hasLoadedMailboxStatus && !composer.sentEmail,
    workspaceId: workspace.id,
    artifactId: artifact.id,
    toAddresses: to.map((recipient) => recipient.email),
    draftKey: `${artifact.version}:${email?.contentRevision ?? ''}`,
    save: composer.save
  });
  const [isChooseClientOpen, setIsChooseClientOpen] = React.useState(false);
  const [pushingEmail, setPushingEmail] = React.useState<PushableEmail | null>(null);

  /**
   * Rows the sender has moved themselves. Named by the field the write carries,
   * which is why every claim below is made where that write is made.
   */
  const claimed = React.useRef(new Set<string>());

  const template = useComposerTemplateState({
    workspace,
    artifact,
    subject,
    session: props.templateSession,
    // The agent chose Cara's redraft over their own subject, so the new one is shown.
    onRedraftAsked: () => claimed.current.delete('subject')
  });

  /** What the artifact was last showing in each row, moved only by the artifact. */
  const shown = React.useRef(rowsOf(email));

  /**
   * Follows the draft when it moves under an open composer -- Cara revising it,
   * or the rail opening a later version of the same email. The rows are seeded
   * at mount and React keeps one instance for the whole artifact, so without
   * this they go on describing the email as it was.
   *
   * A row is taken only where the artifact moved it and the sender has not:
   * mirroring the artifact wholesale would delete what someone is typing, and
   * mirroring nothing is the bug.
   */
  React.useEffect(() => {
    const shownBefore = shown.current;
    const incoming = rowsOf(email);
    shown.current = incoming;

    const held = claimed.current;
    adoptRow(held.has('subject'), shownBefore.subject, incoming.subject, sameText, setSubject);
    adoptRow(held.has('to'), shownBefore.to, incoming.to, sameRecipients, setTo);
    adoptRow(held.has('cc'), shownBefore.cc, incoming.cc, sameRecipients, setCc);
    adoptRow(held.has('bcc'), shownBefore.bcc, incoming.bcc, sameRecipients, setBcc);
    adoptRow(
      held.has('signatureId'),
      shownBefore.signatureId,
      incoming.signatureId,
      sameText,
      setSignatureId
    );
    adoptRow(
      held.has('mailboxAccountId'),
      shownBefore.mailboxAccountId,
      incoming.mailboxAccountId,
      sameText,
      setMailboxAccountId
    );
    adoptRow(
      held.has('attachmentArtifactIds'),
      shownBefore.attachmentArtifactIds,
      incoming.attachmentArtifactIds,
      sameIds,
      setAttachmentArtifactIds
    );
    adoptRow(
      held.has('attachmentFileIds'),
      shownBefore.attachmentFileIds,
      incoming.attachmentFileIds,
      sameIds,
      setAttachmentFileIds
    );
  }, [email]);

  // The editor is uncontrolled after mount, so the body lives in a ref rather
  // than state: re-rendering the composer on every keystroke would fight it.
  const bodyHtmlRef = React.useRef(email?.bodyHtml ?? '');

  // TipTap is filled from the `content` prop without emitting an update, so a
  // body reaching the artifact after mount never arrives at the ref by way of
  // the editor -- and the ref is what the send reads. Seeded only while the ref
  // is still empty, so a body that landed late cannot overwrite words typed in
  // the meantime.
  const arrivedBody = email?.bodyHtml;
  React.useEffect(() => {
    if (arrivedBody && bodyHtmlRef.current === '') {
      bodyHtmlRef.current = arrivedBody;
      template.checkBody(arrivedBody);
    }
  }, [arrivedBody]);

  // The editor below is keyed by version, so a revision remounts it on the
  // incoming letter whatever was in the old one. The ref has to follow it: the
  // send reads the ref, and words no longer on screen are words nobody read.
  const editorKey = `${artifact.id}-${artifact.version}`;
  const bodySeededFor = React.useRef(editorKey);
  React.useEffect(() => {
    if (bodySeededFor.current === editorKey) return;
    bodySeededFor.current = editorKey;
    bodyHtmlRef.current = arrivedBody ?? '';
    template.checkBody(arrivedBody ?? '');
  }, [editorKey, arrivedBody]);

  const { save } = composer;
  const saveEmail = React.useCallback(
    (patch: CaraWorkspaceArtifactEmailInput) => {
      if (bodyHydrating) {
        return;
      }
      // Every write from here is the sender moving a row, and the patch names
      // exactly the rows they moved.
      Object.keys(patch).forEach((field) => claimed.current.add(field));
      setValidationError(null);
      void save(patch);
    },
    [save, bodyHydrating]
  );

  // A `<select>` whose value matches no option displays the first without
  // selecting it, so a draft holding a dead mailbox id would show a live
  // mailbox the send is still refused for. Held locally until an edit or the
  // send persists it.
  const { reconnectAccounts, sendableAccounts, signatures } = composer;

  /**
   * Why a From address cannot be moved to, for the two ways a mailbox stops
   * being one this reader can send from after Cara picked it: its grant died,
   * or it was never theirs. Cara validates the mailbox against the agent it
   * was drafting for, at the time it drafted; a colleague opening the same
   * workspace, or a token that expired since, is a different answer.
   */
  const unsendableSenderReason = (id: string) =>
    reconnectAccounts.some((account) => account.id === id)
      ? 'That mailbox needs reconnecting before it can send, so the sender is unchanged. Reconnect it in Settings, or pick another mailbox.'
      : 'Cara chose a mailbox you cannot send from, so the sender is unchanged. Pick one of your own mailboxes to move this email.';

  /**
   * Whether this composer offers a mailbox at all. A person with none -- none
   * of their own and none shared with them -- gets the card as it was before
   * mailboxes existed, rather than a From row with nothing in it and buttons
   * that can only refuse.
   *
   * A mailbox whose grant has died still counts: it is one they have, the
   * composer is where they are told to reconnect it, and hiding the strip
   * would take that away at the moment it matters. Unresolved counts as
   * absent, so the buttons appear when the answer arrives rather than
   * disappearing once it does.
   */
  const mailboxEnabled = mailboxFlagEnabled && composer.mailboxAccounts.length > 0;
  const reconnectAccount = reconnectAccounts.find((account) => account.id === mailboxAccountId);
  React.useEffect(() => {
    if (
      !email?.mailboxAccountId &&
      composer.mailboxDraft &&
      !claimed.current.has('mailboxAccountId')
    ) {
      setMailboxAccountId(composer.mailboxDraft.mailboxAccountId);
      return;
    }
    // Losing access must not silently change the sender of the reviewed email.
    if (reconnectAccount) return;
    if (sendableAccounts.length === 0) return;
    if (sendableAccounts.some((account) => account.id === mailboxAccountId)) return;
    // isDefault is answered for the signed-in user, so a mailbox a colleague
    // shared never wins this over one of the reader's own.
    const preferred =
      sendableAccounts.find((account) => account.id === composer.mailboxDraft?.mailboxAccountId) ??
      sendableAccounts.find((account) => account.isDefault);
    setMailboxAccountId((preferred ?? sendableAccounts[0]).id);
  }, [
    mailboxAccountId,
    sendableAccounts,
    reconnectAccount,
    composer.mailboxDraft,
    email?.mailboxAccountId
  ]);

  // Cara can revise the From address while this composer is open. That change
  // needs the same confirmation as choosing an address in the dropdown.
  React.useEffect(() => {
    const oldMailbox = composer.mailboxDraft?.mailboxAccountId;
    if (
      // The draft and the mailbox list arrive independently. Answering before
      // the list is known reads an empty list as "none of yours" and says so.
      !composer.loading &&
      !composer.sentEmail &&
      oldMailbox &&
      email?.mailboxAccountId &&
      email.mailboxAccountId !== oldMailbox &&
      !claimed.current.has('mailboxAccountId')
    ) {
      // A mailbox this person cannot send from is not offered as a
      // destination; the sender stays where the draft is and the reason is
      // said outside the dialog, which could not name that mailbox anyway.
      if (sendableAccounts.some((account) => account.id === email.mailboxAccountId)) {
        setPendingMailboxAccountId(email.mailboxAccountId);
        setUnsendableMailboxError(null);
      } else {
        setUnsendableMailboxError(unsendableSenderReason(email.mailboxAccountId));
      }
      setMailboxAccountId(oldMailbox);
    }
  }, [
    email?.mailboxAccountId,
    composer.mailboxDraft,
    composer.sentEmail,
    composer.loading,
    sendableAccounts
  ]);

  const selectedAccount = sendableAccounts.find((account) => account.id === mailboxAccountId);
  const mailClient = mailClientName(selectedAccount?.provider);
  const draftsLocation = draftsLocationName(selectedAccount?.provider);

  /**
   * Whether the mailbox this draft named has left the account list entirely,
   * rather than merely needing reconnecting. Only the id is stored, so there is
   * nothing left to name it by.
   */
  const mailboxMissing =
    !!email?.mailboxAccountId &&
    !composer.loading &&
    ![...sendableAccounts, ...reconnectAccounts].some(
      (account) => account.id === email.mailboxAccountId
    );

  const selectedSignature = signatures.find((signature) => signature.id === signatureId);
  // A draft can name a signature this sender cannot use -- deleted since it was
  // chosen, or a colleague's private one on a draft Cara composed for them in a
  // shared workspace -- and the server refuses that send outright rather than
  // quietly sending unsigned mail. A row out of reach reads as no row at all, so
  // neither this nor the server can tell the two apart; the message says what is
  // true of both. Saying so beside the picker costs the sender a choice they can
  // make now; leaving it to the send costs them the send.
  const signatureMissing = !!signatureId && !selectedSignature && !composer.loading;

  const attachableArtifacts = React.useMemo(
    () =>
      latestByArtifactId(workspace.artifacts.filter((candidate) => candidate.id !== artifact.id)),
    [workspace.artifacts, artifact.id]
  );
  const attachableArtifactsById = React.useMemo(() => {
    const byId = new Map<string, Artifact>();
    attachableArtifacts.forEach((candidate) => byId.set(candidate.id, candidate));
    return byId;
  }, [attachableArtifacts]);

  const artifactDetailsById = React.useMemo(() => {
    const byId = new Map<string, AttachableArtifact>();
    attachedArtifactDetails.forEach((detail) => byId.set(detail.id, detail));
    return byId;
  }, [attachedArtifactDetails]);

  /**
   * The workspace's own copy answers first: it is the version the rest of the
   * composer renders, and it needs no lookup to be named.
   */
  const attachedArtifacts = attachmentArtifactIds.flatMap((id) => {
    const own = attachableArtifactsById.get(id);
    if (own) {
      return [{ id, title: getArtifactTitle(own), origin: undefined }];
    }
    const detail = artifactDetailsById.get(id);
    return detail
      ? [{ id, title: detail.title, origin: artifactOrigin(detail, workspace.id) }]
      : [];
  });
  /**
   * The files this draft already carries, named by the server. A chart Cara
   * generated and attached is not in `workspace.files` -- that list is uploads
   * only, so nothing reaches an email that Cara or the sender did not choose --
   * and would otherwise read as an attachment whose file is gone.
   */
  const attachmentFiles = email?.attachmentFiles;
  /**
   * Uploads made here are laid over the workspace's own list: the workspace
   * query does not refetch on an upload, so a file dropped a second ago would
   * otherwise be an attachment with no name.
   */
  const { uploads } = composer;
  const knownFiles = React.useMemo(() => {
    const known = new Map<string, AttachmentFile>();
    (attachmentFiles ?? []).forEach((file) => known.set(file.id, file));
    (workspace.files ?? []).forEach((file) => known.set(file.id, file));
    uploads.forEach((file) => known.set(file.id, file));
    return known;
  }, [attachmentFiles, workspace.files, uploads]);

  const groupByFileId = React.useMemo(() => {
    const byId = new Map<string, AttachmentGroup>();
    attachmentGroups.forEach((group) => byId.set(group.fileId, group));
    return byId;
  }, [attachmentGroups]);

  // Held in the order the sender attached them, which is the order they will
  // appear on the message. A merged file is an upload like any other on the
  // draft, and only this composer knows to draw it as the group it came from.
  const attachedFiles = attachmentFileIds.flatMap((id) => {
    if (groupByFileId.has(id)) {
      return [];
    }
    const file = knownFiles.get(id);
    return file ? [file] : [];
  });

  const attachedGroups = attachmentFileIds.flatMap((id) => {
    const group = groupByFileId.get(id);
    return group ? [group] : [];
  });

  /**
   * Attachments whose file was deleted from the workspace after the draft named
   * it. The send is held until they are taken off.
   */
  const unresolvedFileIds = attachmentFileIds.filter(
    (id) => !knownFiles.has(id) && !groupByFileId.has(id)
  );

  /**
   * Attached documents nothing can name. The lookup runs under the visibility
   * rules the send is refused under, so this holds the send rather than quietly
   * dropping them. Empty while the lookup is in flight.
   */
  const unresolvedArtifactIds = checkingAttachments
    ? []
    : attachmentArtifactIds.filter(
        (id) => !attachableArtifactsById.has(id) && !artifactDetailsById.has(id)
      );

  const attachedBytes =
    attachedFiles.reduce((total, file) => total + file.size, 0) +
    attachedGroups.reduce((total, group) => total + group.size, 0);

  // What the picker offers: the workspace's uploads and the ones made here, so a
  // file taken off again does not need uploading twice. Not the draft's own
  // attachment files, which can name a generated chart the picker must not
  // offer as a workspace file.
  const workspaceFiles = React.useMemo(() => {
    const offered = new Map<string, AttachmentFile>();
    (workspace.files ?? []).forEach((file) => offered.set(file.id, file));
    uploads.forEach((file) => offered.set(file.id, file));
    // A merged PDF is in the workspace like any upload, but offering it as a row
    // of its own invites attaching a combined file beside the group that is it.
    return [...offered.values()].filter((file) => !groupByFileId.has(file.id));
  }, [workspace.files, uploads, groupByFileId]);

  const {
    clearUploadError,
    searchAttachableArtifacts,
    uploadAttachment,
    uploading,
    attachmentLimitBytes
  } = composer;

  const attachFiles = React.useCallback(
    async (files: File[]) => {
      setAttachmentError(null);
      clearUploadError();

      // The running total, not the saved one: the second of two files that fit
      // individually has to be weighed against the first having been accepted.
      let bytes = attachedBytes;
      const added: string[] = [];
      for (const file of files) {
        const refusal = attachmentRefusal({
          name: file.name,
          size: file.size,
          attachedBytes: bytes,
          limitBytes: attachmentLimitBytes
        });
        if (refusal) {
          setAttachmentError(refusal);
          continue;
        }
        const uploaded = await uploadAttachment(file);
        // One failed upload is almost always the whole batch failing, and
        // pressing on would replace the one readable explanation with the next
        // file's copy of it.
        if (!uploaded) {
          break;
        }
        bytes += uploaded.size;
        added.push(uploaded.id);
      }

      // Whatever did land is saved even when a later file was refused: bytes
      // already in the workspace should not need uploading twice.
      if (added.length === 0) {
        return;
      }
      const next = [...attachmentFileIds, ...added];
      setAttachmentFileIds(next);
      saveEmail({ attachmentFileIds: next });
    },
    [
      attachedBytes,
      attachmentFileIds,
      attachmentLimitBytes,
      clearUploadError,
      saveEmail,
      uploadAttachment
    ]
  );

  const offeredArtifacts = React.useRef(new Map<string, AttachableArtifact>());

  /**
   * Names the attachments a stored draft arrived with: it holds bare IDs, and
   * ones from another workspace are absent from `workspace.artifacts`. Each ID
   * is asked about once — a lookup that comes back without it leaves it unnamed
   * on purpose, which is the state the send is blocked on.
   */
  const { resolveAttachedArtifacts } = composer;
  const artifactsLookedUp = React.useRef(new Set<string>());
  // Counted rather than a boolean: the effect re-runs whenever its inputs are
  // rebuilt, and a re-run that finds every ID already asked about returns
  // without starting anything. A flag cleared only by the run that set it would
  // then be stranded on by the superseded lookup, leaving the send blocked on a
  // check that finished.
  const lookupsInFlight = React.useRef(0);
  React.useEffect(() => {
    const unnamed = attachmentArtifactIds.filter(
      (id) =>
        !attachableArtifactsById.has(id) &&
        !artifactDetailsById.has(id) &&
        !artifactsLookedUp.current.has(id)
    );
    if (unnamed.length === 0) {
      return;
    }
    unnamed.forEach((id) => artifactsLookedUp.current.add(id));

    let current = true;
    lookupsInFlight.current += 1;
    setCheckingAttachments(true);
    resolveAttachedArtifacts(unnamed)
      .then((found) => {
        // Deliberately not gated on `current`. Each ID is claimed in
        // artifactsLookedUp before the query goes out and never claimed twice,
        // so a superseded run's answer is the only answer those IDs will get:
        // discarding it strands them unnamed and blocks the send. Safe out of
        // order — the write de-dupes by ID.
        setAttachedArtifactDetails((known) => [
          ...known,
          ...found.filter((detail) => !known.some((seen) => seen.id === detail.id))
        ]);
      })
      .catch(() => {
        if (!current) return;
        // The attachments stay unnamed, so the send stays blocked. Saying which
        // way it failed matters: "we could not look" and "it is not there any
        // more" ask different things of the sender.
        setAttachmentError('We could not check what this email carries. Reload before sending.');
      })
      .finally(() => {
        lookupsInFlight.current -= 1;
        if (lookupsInFlight.current === 0) setCheckingAttachments(false);
      });

    return () => {
      current = false;
    };
  }, [
    attachmentArtifactIds,
    attachableArtifactsById,
    artifactDetailsById,
    resolveAttachedArtifacts
  ]);

  /**
   * A failure is thrown rather than described: `uploadAttachment` has already
   * put the reason on screen, and the picker only needs to know the rows are
   * not coming.
   */
  const uploadPickedFiles = React.useCallback(
    async (files: File[]): Promise<AttachmentFile[]> => {
      setAttachmentError(null);
      clearUploadError();
      const stored: AttachmentFile[] = [];
      for (const file of files) {
        const uploaded = await uploadAttachment(file);
        if (!uploaded) {
          throw new Error(`“${file.name}” was not stored.`);
        }
        stored.push(uploaded);
      }
      return stored;
    },
    [clearUploadError, uploadAttachment]
  );

  /**
   * The version comes from the workspace on screen because that is the only
   * place a version is known: the picker's rows carry titles and ids.
   */
  const mergeGroup = useAttachmentGroupMerge({
    workspaceId: workspace.id,
    latestVersionOf: (artifactId) => attachableArtifactsById.get(artifactId)?.version,
    storeFile: uploadAttachment
  });

  // An email cannot be an attachment on itself. The type filter keeps emails
  // out of the results already, so this holds the line only for the day that
  // list widens.
  const searchAttachableDocuments = React.useCallback(
    (query: string) =>
      searchAttachableArtifacts(query).then((candidates) => {
        const offered = candidates.filter((candidate) => candidate.id !== artifact.id);
        // Every document the picker can offer passes through here, which is the
        // one moment a cross-workspace document is named to this composer
        // without a lookup of its own.
        offered.forEach((candidate) => offeredArtifacts.current.set(candidate.id, candidate));
        return offered;
      }),
    [artifact.id, searchAttachableArtifacts]
  );

  /**
   * One write rather than two: the pick is one decision, and a draft stored
   * holding half of it is a draft that can be sent holding half of it.
   */
  const attachPicked = (picked: {
    artifactIds: string[];
    fileIds: string[];
    groups: AttachmentGroup[];
    replacedGroupFileIds: string[];
    consumedArtifactIds: string[];
    consumedFileIds: string[];
  }) => {
    setAttachmentError(null);
    // A document folded into a combined PDF travels inside it, so leaving it
    // attached as well would send the recipient the same pages twice.
    const keptArtifactIds = attachmentArtifactIds.filter(
      (id) => !picked.consumedArtifactIds.includes(id)
    );
    const nextArtifactIds = [
      ...keptArtifactIds,
      ...picked.artifactIds.filter((id) => !keptArtifactIds.includes(id))
    ];
    // A group the sender edited or dissolved leaves as its old merged file and
    // comes back — if at all — as a newly merged one, so the outgoing id is
    // dropped before the incoming ones are appended.
    const keptFileIds = attachmentFileIds.filter(
      (id) => !picked.replacedGroupFileIds.includes(id) && !picked.consumedFileIds.includes(id)
    );
    const groupFileIds = picked.groups.map((group) => group.fileId);
    const nextFileIds = [
      ...keptFileIds,
      ...[...picked.fileIds, ...groupFileIds].filter((id) => !keptFileIds.includes(id))
    ];
    setAttachmentGroups((known) => [
      ...known.filter((group) => !picked.replacedGroupFileIds.includes(group.fileId)),
      ...picked.groups
    ]);
    setEditingGroupFileId(null);
    setAttachmentArtifactIds(nextArtifactIds);
    setAttachmentFileIds(nextFileIds);
    setAttachedArtifactDetails((known) => [
      ...known,
      ...picked.artifactIds
        .flatMap((id) => {
          const offered = offeredArtifacts.current.get(id);
          return offered ? [offered] : [];
        })
        .filter((detail) => !known.some((seen) => seen.id === detail.id))
    ]);
    saveEmail({
      attachmentArtifactIds: nextArtifactIds,
      attachmentFileIds: nextFileIds
    });
  };

  const detachArtifact = (artifactId: string) => {
    setAttachmentError(null);
    const next = attachmentArtifactIds.filter((id) => id !== artifactId);
    setAttachmentArtifactIds(next);
    saveEmail({ attachmentArtifactIds: next });
  };

  const detachFile = (fileId: string) => {
    setAttachmentError(null);
    clearUploadError();
    const next = attachmentFileIds.filter((id) => id !== fileId);
    setAttachmentFileIds(next);
    setAttachmentGroups((known) => known.filter((group) => group.fileId !== fileId));
    saveEmail({ attachmentFileIds: next });
  };

  /**
   * The merged file stays in the workspace — it is an upload, and uploads are
   * kept — but the email carries its parts instead.
   */
  const dissolveGroup = (group: AttachmentGroup) => {
    setAttachmentError(null);
    const artifactIds = group.members
      .filter((member) => member.kind === 'artifact')
      .map((member) => member.id);
    const fileIds = group.members
      .filter((member) => member.kind === 'file')
      .map((member) => member.id);
    const nextArtifactIds = [
      ...attachmentArtifactIds,
      ...artifactIds.filter((id) => !attachmentArtifactIds.includes(id))
    ];
    const withoutMerged = attachmentFileIds.filter((id) => id !== group.fileId);
    const nextFileIds = [...withoutMerged, ...fileIds.filter((id) => !withoutMerged.includes(id))];
    setAttachmentArtifactIds(nextArtifactIds);
    setAttachmentFileIds(nextFileIds);
    setAttachmentGroups((groups) => groups.filter((held) => held.fileId !== group.fileId));
    saveEmail({ attachmentArtifactIds: nextArtifactIds, attachmentFileIds: nextFileIds });
  };

  const openPicker = (editingFileId: string | null) => {
    setEditingGroupFileId(editingFileId);
    setPickerOpening((opening) => opening + 1);
    setPickerOpen(true);
  };

  const editGroup = (group: AttachmentGroup) => {
    setAttachmentError(null);
    openPicker(group.fileId);
  };

  // A scheduled email opens for edits only after Edit, and stays scheduled.
  // Only the person who scheduled it may edit or move it; the server refuses
  // everyone else.
  const scheduled = composer.sentEmail ? null : composer.scheduledEmail;
  const scheduledEmailId = scheduled?.id;
  React.useEffect(() => setEditingScheduled(false), [scheduledEmailId]);
  // A schedule dialog has nothing left to do once the email is sent (from
  // Outlook, say) or its scheduled delivery has started. Its refusal stays,
  // under the composer, to say why.
  const isEmailSending = !!composer.sentEmail;
  React.useEffect(() => {
    if (isEmailSending) setScheduleDialog(null);
  }, [isEmailSending]);
  React.useEffect(() => {
    if (!scheduledEmailId) setCancelScheduleOpen(false);
  }, [scheduledEmailId]);
  const isScheduledByReader = !!scheduled && scheduled.sentByUserId === currentAgencyUserId;
  const isScheduledActionRunning = !!scheduled && (mailboxActionPending || composer.scheduling);
  const isScheduledAndLocked =
    !!scheduled && (!editingScheduled || !isScheduledByReader || isScheduledActionRunning);

  const { handlers: dropHandlers, isDragging } = useFileDrop({
    onDrop: (files) => void attachFiles(files),
    disabled: !!composer.sentEmail || isScheduledAndLocked
  });

  /**
   * A drop the writing area already consumed is not an attachment: the image
   * placeholder swallows its own drops, and ProseMirror marks the content drops
   * it inserted.
   */
  const onComposerDrop = (event: React.DragEvent) => {
    if (event.defaultPrevented) {
      return;
    }
    dropHandlers.onDrop(event);
  };

  const malformed = [...to, ...cc, ...bcc].some((recipient) => !isEmailShaped(recipient.email));

  const disabledReason = sendDisabledReason({
    loading: composer.loading,
    loadingMailboxDraft: composer.loadingMailboxDraft,
    mailboxActionPending,
    changingSender: !!pendingMailboxAccountId || switchingMailbox,
    mailboxDraftLoadFailed: composer.mailboxDraftLoadFailed,
    bodyTooLarge: bodyError !== null,
    sendableAccountCount: sendableAccounts.length,
    reconnectAccountCount: reconnectAccounts.length,
    selectedAccountNeedsReconnect: !!reconnectAccount,
    to,
    cc,
    bcc,
    uploadingCount: uploading.length,
    unresolvedAttachmentCount: unresolvedFileIds.length,
    checkingAttachments,
    unavailableArtifactCount: unresolvedArtifactIds.length
  });

  const sendReason = disabledReason ?? template.sendReason;
  // The "Cara is writing" notice already explains the wait, so it stays a hover title only.
  const sendReasonShownUnderSend = sendReason === WAIT_FOR_CARA_REASON ? undefined : sendReason;

  // Changing sender is refused for the same reasons sending is: mailbox work
  // already in flight, or a status check that has not come back. Named once so
  // the From select and sendDisabledReason's first branches cannot drift into
  // disagreeing about whether the mailbox is settled.
  const mailboxBusy =
    mailboxActionPending ||
    composer.loadingMailboxDraft ||
    composer.mailboxDraftLoadFailed ||
    switchingMailbox ||
    composer.savingToMailbox ||
    composer.sending;

  const composedEmail = (): CaraWorkspaceArtifactEmailInput => ({
    subject,
    bodyHtml: bodyHtmlRef.current,
    to: toRecipientInput(to),
    cc: toRecipientInput(cc),
    bcc: toRecipientInput(bcc),
    signatureId: signatureId || null,
    mailboxAccountId: mailboxAccountId || null,
    attachmentArtifactIds,
    attachmentFileIds
  });

  /**
   * Why this composition may not be parked in a mailbox as it stands. Checked
   * by `flushForCommit`, which guards the two paths that put it there: saving
   * the draft into a mailbox, and switching it to another one. Sending has its
   * own gate in `disabledReason`; the server refuses either way.
   *
   * Re-checked at the click rather than trusted from the disabled button: the
   * confirm dialog sits between the click and the write, and the draft can move
   * under it.
   *
   * Callers that reached here through requireMailboxChoice have already been
   * turned back if the draft sits elsewhere, so the first branch cannot fire
   * for them. It stays because this function's job is to refuse, and it is
   * callable on its own defaults: a redundant refusal costs a click, while a
   * missing one parks mail in a mailbox the sender did not choose.
   */
  const blockingReason = (
    draft = composer.mailboxDraft,
    allowEmptyRecipients = false
  ): string | undefined => {
    if (draft && draft.mailboxAccountId !== mailboxAccountId) {
      return 'Confirm removal of the old mailbox draft before continuing.';
    }
    if (!selectedAccount) {
      return 'Pick a connected mailbox to send from.';
    }
    if (!allowEmptyRecipients && to.length === 0) {
      return 'Add at least one recipient.';
    }
    if (malformed) {
      return 'Fix the highlighted address before sending.';
    }
    if (uploading.length > 0) {
      return 'Wait for the attachment to finish uploading.';
    }
    if (unresolvedFileIds.length > 0) {
      return 'One of the attachments is no longer in this workspace. Remove it first.';
    }
    if (checkingAttachments) {
      return 'Still checking what this email carries. Try again in a moment.';
    }
    if (unresolvedArtifactIds.length > 0) {
      return 'One of the attached documents is no longer available to you. Remove it first.';
    }
    return undefined;
  };

  /**
   * Flushes the whole composer, not just the last edit: the server works from
   * the stored artifact, so an unsaved keystroke would silently not travel.
   */
  const flushForCommit = async (
    draft = composer.mailboxDraft,
    { allowEmptyRecipients = false }: { allowEmptyRecipients?: boolean } = {}
  ): Promise<boolean> => {
    if (bodyError) {
      return false;
    }
    const blocked = blockingReason(draft, allowEmptyRecipients);
    if (blocked) {
      setValidationError(blocked);
      return false;
    }
    setValidationError(null);
    return composer.save(composedEmail());
  };

  const withMailboxAction = async (action: () => Promise<void>) => {
    if (mailboxAction.current || composer.sentEmail) return;
    mailboxAction.current = true;
    setMailboxActionPending(true);
    try {
      await action();
    } finally {
      mailboxAction.current = false;
      setMailboxActionPending(false);
    }
  };

  /**
   * Opens the confirmation when the draft sits in a mailbox other than the one
   * now chosen, and answers whether the caller must stop.
   *
   * A destination this person cannot send from never reaches the dialog. Cara
   * can set the From address to a mailbox the reader has no grant for, and the
   * dialog would then offer to create a draft in a mailbox it cannot name while
   * the server refused the confirm. That case stops here too: the sender stays
   * where the draft is, and the reason is said outside the dialog.
   */
  const requireMailboxChoice = (status: MailboxStatus, nextId: string) => {
    if (!status.mailboxDraft || status.mailboxDraft.mailboxAccountId === nextId) {
      // Nothing is in the way of this sender, so a refusal of an earlier one no
      // longer describes the composer and must not outlive it on screen.
      setUnsendableMailboxError(null);
      return false;
    }
    setMailboxAccountId(status.mailboxDraft.mailboxAccountId);
    if (!sendableAccounts.some((account) => account.id === nextId)) {
      setUnsendableMailboxError(unsendableSenderReason(nextId));
      return true;
    }
    setUnsendableMailboxError(null);
    setPendingMailboxAccountId(nextId);
    return true;
  };

  const handleFromChange = (nextId: string) =>
    withMailboxAction(async () => {
      const status = await composer.refreshMailboxStatus();
      if (!status || status.sentEmail || requireMailboxChoice(status, nextId)) return;
      setMailboxAccountId(nextId);
      saveEmail({ mailboxAccountId: nextId || null });
    });

  const handleSaveToMailbox = () =>
    withMailboxAction(async () => {
      const status = await composer.refreshMailboxStatus();
      if (!status || status.sentEmail || requireMailboxChoice(status, mailboxAccountId)) return;
      if (!(await flushForCommit(status.mailboxDraft)) || !selectedAccount) return;
      await composer.saveToMailbox(selectedAccount.id);
    });

  const handleSend = () =>
    withMailboxAction(async () => {
      if (!selectedAccount || sendReason) return;
      if (!(await composer.send(selectedAccount.id, composedEmail()))) {
        const status = await composer.refreshMailboxStatus();
        if (status) requireMailboxChoice(status, mailboxAccountId);
      }
    });

  const handleSchedule = (sendAt: Date) =>
    withMailboxAction(async () => {
      if (!selectedAccount || sendReason) return;
      composer.clearSendError();
      if (await composer.scheduleSend(selectedAccount.id, composedEmail(), sendAt)) {
        setScheduleDialog(null);
      }
    });

  // Send now carries no composition, so the composer's own words are saved
  // first and the move waits behind that save.
  const handleScheduledSendNow = () =>
    withMailboxAction(async () => {
      if (template.sendReason || !(await flushForCommit())) return;
      await composer.sendScheduledEmailNow();
    });

  const handleReschedule = async (sendAt: Date) => {
    if (await composer.rescheduleSend(sendAt)) setScheduleDialog(null);
  };

  const openScheduleDialog = (mode: 'schedule' | 'reschedule') => {
    composer.clearScheduleError();
    setScheduleDialog(mode);
  };

  const openCancelSchedule = () => {
    setIsCancelScheduleRefused(false);
    setCancelScheduleOpen(true);
  };

  // Closes on the server's answer; a refusal keeps the dialog open to say why.
  const confirmCancelSchedule = async () => {
    setIsCancelScheduleRefused(false);
    if (await composer.cancelSchedule()) setCancelScheduleOpen(false);
    else setIsCancelScheduleRefused(true);
  };

  const confirmMailboxSwitch = (createDraft: boolean) =>
    withMailboxAction(async () => {
      const nextId = pendingMailboxAccountId;
      if (!nextId || switchingMailbox) return;
      setSwitchingMailbox(true);
      try {
        const status = await composer.refreshMailboxStatus();
        if (!status || status.sentEmail) return;
        // Confirmation covers the copy named in this dialog. If someone replaced
        // it while the dialog was open, show the new state for another review.
        if (!status.mailboxDraft || status.mailboxDraft.id !== composer.mailboxDraft?.id) {
          setValidationError(
            'The mailbox draft changed. Review its current location before choosing again.'
          );
          if (status.mailboxDraft) setMailboxAccountId(status.mailboxDraft.mailboxAccountId);
          else setPendingMailboxAccountId(null);
          return;
        }
        if (!(await flushForCommit(status.mailboxDraft, { allowEmptyRecipients: !createDraft }))) {
          return;
        }
        if (!(await composer.switchMailboxDraft(nextId, createDraft))) return;
        claimed.current.add('mailboxAccountId');
        setMailboxAccountId(nextId);
        setPendingMailboxAccountId(null);
      } finally {
        setSwitchingMailbox(false);
      }
    });

  const cancelMailboxSwitch = () => {
    if (mailboxAction.current) return;
    setPendingMailboxAccountId(null);
    if (email?.mailboxAccountId !== mailboxAccountId) saveEmail({ mailboxAccountId });
  };

  /**
   * A refused save reads as idle: the reason is already an ErrorMessage above
   * the send bar, and a second, wordless marker only asks which to believe.
   */
  const draftSaveStatus: SaveStatus =
    composer.saveStatus === 'saving' || composer.saveStatus === 'saved'
      ? composer.saveStatus
      : 'idle';

  const sent = composer.sentEmail;
  const locked = !!sent || composer.sending || isScheduledAndLocked;
  const canChooseAmsClient = !!props.canUpdate && !locked;

  const templatePickDisabledReason = template.isCaraWriting
    ? WAIT_FOR_CARA_REASON
    : locked
      ? "This email can't be changed now."
      : undefined;
  const delivered = sent?.status === OutboundEmailStatus.Sent;
  const sentEmailAmsLog = useSentEmailAmsLog({
    enabled: isAmsLoggingEnabled && delivered,
    artifactId: artifact.id,
    sentEmailId: sent?.id,
    sentAt: sent?.sentAt
  });
  const sentAmsLog = sentEmailAmsLog.amsLog;
  const queued = sent?.status === OutboundEmailStatus.Sending;
  const scheduledFor = scheduled?.scheduledFor ? new Date(scheduled.scheduledFor) : null;
  const scheduleStatusLine = !isScheduledByReader
    ? scheduledByAnotherUserMessage(scheduled?.sentBy?.name)
    : editingScheduled
      ? `Changes save as you type. It still sends ${scheduledFor ? formatSendTime(scheduledFor) : 'at its scheduled time'}.`
      : 'To change anything, choose Edit. It stays scheduled while you edit.';

  // The selected mailbox stays visible as a disabled From option when it needs
  // reconnecting. This notice explains how to restore access; other disconnected
  // mailboxes remain absent from the options.
  const mailboxNotice = () => {
    if (composer.loading) {
      return undefined;
    }
    if (reconnectAccounts.length > 0) {
      return (
        <Description>
          {reconnectAccounts.map((account) => account.identity.email).join(', ')}{' '}
          {reconnectAccounts.length === 1 ? 'lost its connection' : 'lost their connection'} to Cara
          and cannot send until reconnected.{' '}
          <Link
            href="/app/settings/mailbox"
            className="underline decoration-neutral-400 dark:decoration-neutral-500 hover:decoration-neutral-600 dark:hover:decoration-neutral-400"
          >
            Reconnect in Settings
          </Link>
          .
        </Description>
      );
    }
    if (sendableAccounts.length === 0) {
      return (
        <Description>
          Connect a mailbox in Settings to send from Cara. You can keep drafting here in the
          meantime.
        </Description>
      );
    }
    // Grey rather than red: the substitution is already made, and the words the
    // sender wrote are untouched.
    if (mailboxMissing) {
      return (
        <Description>
          The mailbox this draft was written for is no longer connected to Cara, so it will go from
          the one selected here.
        </Description>
      );
    }
    return undefined;
  };

  return (
    <ArtifactFocusShell
      title={subject}
      version={artifact.version}
      onClose={props.onClose}
      closeLabel="Close composer"
      onTitleChange={(value) => {
        // Claimed on the keystroke rather than at the blur that saves it, so a
        // revision landing mid-sentence cannot take the sentence away.
        claimed.current.add('subject');
        template.recordSubjectEdit();
        setSubject(value);
      }}
      onTitleCommit={() => {
        // A blur commits whether or not the subject moved, and a save marks the email edited.
        const isSubjectChanged = subject !== (email?.subject ?? '');
        if (isSubjectChanged) saveEmail({ subject });
      }}
      titleLabel="Subject"
      titlePlaceholder="Untitled email"
      titleHasFieldReferences
      titleDisabled={locked}
      aside={
        sent ? (
          <div className="flex min-w-0 items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
            <Badge color={delivered ? 'green' : 'blue'}>{delivered ? 'Sent' : 'Sending'}</Badge>
            <span className="truncate">
              {delivered
                ? 'This email has left the building. Ask Cara for a new draft to send another.'
                : 'This email is queued to send. Leaving this page will not cancel it.'}
            </span>
          </div>
        ) : scheduled ? (
          <ScheduledEmailStatus scheduledEmail={scheduled} provider={selectedAccount?.provider} />
        ) : undefined
      }
      headerActions={
        <div className="flex shrink-0 flex-col items-end gap-2">
          {composer.loading ? (
            <Spinner size="size-4" color="neutral" />
          ) : (
            <SaveStatusIndicator status={draftSaveStatus} className="mt-1 text-right" />
          )}
          {scheduled && isScheduledByReader && mailboxEnabled && (
            <ScheduledEmailActions
              editing={editingScheduled}
              canChangeSendTime={scheduledSendFlagEnabled}
              busy={isScheduledActionRunning}
              onCancel={openCancelSchedule}
              onToggleEdit={() => setEditingScheduled((editing) => !editing)}
              onReschedule={() => openScheduleDialog('reschedule')}
              sendNowDisabledReason={template.sendReason}
              onSendNow={() => void handleScheduledSendNow()}
            />
          )}
          {!scheduled && !delivered && mailboxEnabled && !isBulkEmail && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {/* No confirm step: saving the same email again updates the copy
                  in the mailbox rather than leaving two. */}
              <Button
                type="button"
                outline
                disabled={
                  !!sent || composer.sending || !!disabledReason || composer.savingToMailbox
                }
                title={disabledReason}
                onClick={() => void handleSaveToMailbox()}
              >
                <InboxArrowDownIcon className="size-4" />
                {composer.savingToMailbox
                  ? `Saving to ${mailClient}…`
                  : composer.mailboxDraft
                    ? `Update draft in ${mailClient}`
                    : `Save to ${mailClient}`}
              </Button>
              <EmailSendButton
                disabledReason={sendReason}
                sending={composer.sending || queued}
                sendAfter={composer.sendAfter}
                cancelling={composer.cancellingSend}
                onSend={handleSend}
                onCancel={composer.cancelSend}
                schedule={
                  scheduledSendFlagEnabled
                    ? {
                        onSchedule: (sendAt) => void handleSchedule(sendAt),
                        onPickDateAndTime: () => openScheduleDialog('schedule')
                      }
                    : undefined
                }
              />
            </div>
          )}
        </div>
      }
    >
      <div
        className="relative flex min-h-0 flex-1 flex-col"
        onDragEnter={dropHandlers.onDragEnter}
        onDragLeave={dropHandlers.onDragLeave}
        onDragOver={dropHandlers.onDragOver}
        onDrop={onComposerDrop}
      >
        {isDragging && <AttachmentDropOverlay />}
        <Dialog
          open={mailboxEnabled && pendingMailboxAccountId !== null}
          onClose={cancelMailboxSwitch}
          size="lg"
        >
          <DialogTitle>Change the From mailbox?</DialogTitle>
          <DialogBody>
            {composer.mailboxDraft ? (
              <p>
                The draft in <strong>{composer.mailboxDraft.fromAddress}</strong> will be deleted
                from that mailbox. Your email will stay in Cara, so you can put it in another
                mailbox whenever you like.
              </p>
            ) : (
              <p>
                The old mailbox draft was removed. Your email is still in Cara; finish choosing its
                new sender below.
              </p>
            )}
            <p className="mt-3">
              Create a draft in{' '}
              {/* The address is looked up rather than remembered, and a
                refresh while this dialog is open can drop the account it
                names -- a reconnect, or the refresh that follows a failed
                switch. The question still has to read as a sentence. */}
              <strong>
                {sendableAccounts.find((account) => account.id === pendingMailboxAccountId)
                  ?.identity.email ?? 'the selected mailbox'}
              </strong>
              , or keep it only in Cara? A new mailbox draft will use the content shown here.
            </p>
            {email?.source && (
              <p className="mt-3">
                The new email will not keep reply or forward threading, quoted content, or
                attachments that exist only in the old mailbox draft. Copy any content you need into
                Cara and add those attachments here before switching.
              </p>
            )}
            {(composer.mailboxDraftError || composer.saveError || validationError) && (
              <ErrorMessage className="mt-3">
                {composer.mailboxDraftError || composer.saveError || validationError}
              </ErrorMessage>
            )}
          </DialogBody>
          <DialogActions>
            <Button plain disabled={mailboxActionPending} onClick={cancelMailboxSwitch}>
              Cancel
            </Button>
            <Button
              outline
              disabled={mailboxActionPending}
              onClick={() => void confirmMailboxSwitch(false)}
            >
              Keep only in Cara
            </Button>
            <Button disabled={mailboxActionPending} onClick={() => void confirmMailboxSwitch(true)}>
              Create in new mailbox
            </Button>
          </DialogActions>
        </Dialog>
        {scheduleDialog && (
          <ScheduleSendDialog
            mode={scheduleDialog}
            currentSendAt={scheduledFor}
            fromAddress={selectedAccount?.identity.email}
            provider={selectedAccount?.provider}
            submitting={composer.scheduling || mailboxActionPending}
            error={composer.scheduleError}
            onSubmit={(sendAt) =>
              void (scheduleDialog === 'reschedule'
                ? handleReschedule(sendAt)
                : handleSchedule(sendAt))
            }
            onClose={() => setScheduleDialog(null)}
          />
        )}
        <CancelScheduleDialog
          open={cancelScheduleOpen}
          cancelling={composer.scheduling}
          error={isCancelScheduleRefused ? composer.scheduleError : null}
          onConfirm={() => void confirmCancelSchedule()}
          onClose={() => setCancelScheduleOpen(false)}
        />
        {/* Deliberately not inside the attachments row: Headless UI's `Field`
            hands its own label down through context, and a checkbox rendered
            under one is announced as "Attachments" rather than by the document
            it ticks. */}
        <AttachmentPickerDialog
          key={pickerOpening}
          open={pickerOpen}
          onClose={() => {
            setPickerOpen(false);
            setEditingGroupFileId(null);
          }}
          currentWorkspaceId={workspace.id}
          attachedArtifactIds={attachmentArtifactIds}
          attachedFileIds={attachmentFileIds}
          attachedBytes={attachedBytes}
          limitBytes={attachmentLimitBytes}
          workspaceFiles={workspaceFiles}
          agencyProducerInformations={props.agencyProducerInformations}
          userProducerInformations={props.userProducerInformations}
          searchArtifacts={searchAttachableDocuments}
          uploadFiles={uploadPickedFiles}
          mergeGroup={mergeGroup}
          attachedGroups={attachmentGroups}
          editingGroupFileId={editingGroupFileId}
          onConfirm={attachPicked}
        />
        <AnimatePresence>
          {isChooseClientOpen && amsMatch.match && (
            <ChooseClientDialog
              mode="compose"
              workspaceId={workspace.id}
              match={amsMatch.match}
              toAddresses={to.map((recipient) => recipient.email)}
              currentClient={amsClientLoggedTo(amsMatch.match, amsMatch.choice)}
              onChoose={async ({ target, displayName, filingTarget }) =>
                (await amsMatch.saveChoice(
                  { target, ...(filingTarget ? { filingTarget } : {}) },
                  { target, displayName, filingTarget }
                ))
                  ? null
                  : "Couldn't save the client. Try again."
              }
              onSkip={() => void amsMatch.saveChoice({ target: null }, { target: null })}
              onClose={() => setIsChooseClientOpen(false)}
            />
          )}
        </AnimatePresence>
        {/* Kept out of the scrolling column: a reason the send is off, found by
            scrolling to it, is a reason nobody read. */}
        <div className="mt-3 shrink-0 space-y-1 border-b border-neutral-200 dark:border-neutral-700 pb-3">
          {/* Wrapped in a Field because ErrorMessage is a Headless description,
            which throws outside one — and a send refused for a reason nobody
            can read is the same as a send refused for no reason. */}
          <Field className="empty:hidden space-y-1">
            {composer.loadError && <ErrorMessage>{composer.loadError}</ErrorMessage>}
            {composer.saveError && <ErrorMessage>{composer.saveError}</ErrorMessage>}
            {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
            {bodyError && <ErrorMessage>{bodyError}</ErrorMessage>}
            {attachmentError && <ErrorMessage>{attachmentError}</ErrorMessage>}
            {composer.uploadError && <ErrorMessage>{composer.uploadError}</ErrorMessage>}
            {composer.sendError && <ErrorMessage>{composer.sendError}</ErrorMessage>}
            {!scheduleDialog && composer.scheduleError && (
              <ErrorMessage>{composer.scheduleError}</ErrorMessage>
            )}
            {composer.mailboxDraftError && (
              <ErrorMessage>{composer.mailboxDraftError}</ErrorMessage>
            )}
            {unsendableMailboxError && <ErrorMessage>{unsendableMailboxError}</ErrorMessage>}
            {mailboxEnabled && composer.mailboxDraftLoadFailed && (
              <Button
                plain
                disabled={composer.loadingMailboxDraft || mailboxActionPending}
                onClick={() => void composer.refreshMailboxStatus()}
              >
                Retry mailbox check
              </Button>
            )}
          </Field>
          {sent && sentAmsLog && (
            <SentEmailAmsResult
              amsLog={sentAmsLog}
              onPush={() =>
                setPushingEmail({
                  id: sent.id,
                  subject,
                  recipients: to.map((recipient) => recipient.name || recipient.email).join(', '),
                  toAddresses: to.map((recipient) => recipient.email),
                  sentAt: sent.sentAt ?? null,
                  amsLog: sentAmsLog
                })
              }
            />
          )}
          <PushEmailToAms
            email={pushingEmail}
            onAmsLogChange={sentEmailAmsLog.showAmsLog}
            onAmsLogStale={sentEmailAmsLog.readAgain}
            onClose={() => setPushingEmail(null)}
          />
          {sentEmailAmsLog.hasFailed && <SentEmailAmsUnknown />}
          {!sent && (
            <>
              {/* The link is the provider's own and is legitimately absent —
                nothing here may render an anchor to an empty string, which
                lands the reader back on this page. A scheduled email shows
                only its Scheduled state: its send replaces this draft. */}
              {composer.mailboxDraft && !scheduled && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-neutral-700 dark:text-neutral-300">
                  <Badge color="blue">In {mailClient}</Badge>
                  <span>
                    Waiting in the {draftsLocation} of {composer.mailboxDraft.fromAddress}. Not
                    sent.
                  </span>
                  {composer.mailboxDraft.webLink && (
                    <Link
                      href={composer.mailboxDraft.webLink}
                      target="_blank"
                      rel="noreferrer"
                      className="underline decoration-neutral-400 dark:decoration-neutral-500 hover:decoration-neutral-600 dark:hover:decoration-neutral-400"
                    >
                      Open it in {mailClient}
                    </Link>
                  )}
                </div>
              )}
              {/* Every reason the send button is off is a reason about sending,
                so with nothing to send from there is nothing here to explain. */}
              {mailboxEnabled && !isBulkEmail && (
                <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                  {scheduled
                    ? scheduleStatusLine
                    : (sendReasonShownUnderSend ??
                      'Send queues this email with 5 seconds to cancel. Leaving this page will not cancel it.')}
                </span>
              )}
            </>
          )}
        </div>

        {/* The composer's one scrolling element: ledger, letter and signature
            move together, so the writing area never grows a bar of its own. */}
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {/* At least as tall as the viewport, so a short draft has a full page
              to fill before anything scrolls. */}
          <div className="flex min-h-full flex-col pt-3">
            <Fieldset disabled={locked} className="flex min-h-0 flex-1 flex-col">
              {isBulkEmail && (
                <ComposerRow label="From">
                  <span className="text-sm text-neutral-900 dark:text-white">
                    {props.bulkSendFromAddress}
                  </span>
                </ComposerRow>
              )}
              {mailboxEnabled && !isBulkEmail && (
                <ComposerRow label="From" description={mailboxNotice()}>
                  <div className="relative flex items-center">
                    <Headless.Select
                      value={mailboxAccountId}
                      disabled={mailboxBusy || !!scheduled}
                      title={
                        scheduled
                          ? 'This email is scheduled. Cancel the schedule to change its sender.'
                          : undefined
                      }
                      onChange={(e) => void handleFromChange(e.target.value)}
                      aria-label="Send from mailbox"
                      className={BARE_SELECT}
                    >
                      {reconnectAccount && (
                        <option value={reconnectAccount.id} disabled>
                          {reconnectAccount.identity.email} (needs reconnect)
                        </option>
                      )}
                      {sendableAccounts.length === 0 && !reconnectAccount && (
                        <option value="">
                          {reconnectAccounts.length > 0
                            ? 'Mailbox disconnected'
                            : 'No connected mailbox'}
                        </option>
                      )}
                      {sendableAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.identity.displayName
                            ? `${account.identity.displayName} <${account.identity.email}>`
                            : account.identity.email}
                        </option>
                      ))}
                    </Headless.Select>
                    <ChevronDownIcon
                      className="pointer-events-none absolute right-0 size-4 text-neutral-400 dark:text-neutral-500"
                      aria-hidden="true"
                    />
                  </div>
                </ComposerRow>
              )}

              <ComposerRow
                label="To"
                description={
                  <EmailAmsLine
                    match={amsMatch.match}
                    choice={amsMatch.choice}
                    isMatching={amsMatch.isMatching}
                    hasFailed={amsMatch.hasFailed}
                    toAddressCount={to.length}
                    isScheduled={!!scheduled}
                    canChoose={canChooseAmsClient}
                    onRetry={amsMatch.retry}
                    onSkip={() => void amsMatch.saveChoice({ target: null }, { target: null })}
                    onUndoSkip={() => void amsMatch.saveChoice(null, null)}
                    onChooseClient={() => setIsChooseClientOpen(true)}
                  />
                }
                trailing={
                  !ccBccShown && (
                    <button
                      type="button"
                      className="mt-1 shrink-0 text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200"
                      onClick={() => {
                        setCcBccShown(true);
                        setCcBccFocus(true);
                      }}
                    >
                      Cc Bcc
                    </button>
                  )
                }
              >
                {/* A bulk email goes to its row on the bulk draft's page, which is what the
                    agent reviewed there. */}
                {isBulkEmail ? (
                  <span className="text-sm text-neutral-900 dark:text-white">
                    {to.map(({ email }) => email).join(', ')}
                  </span>
                ) : (
                  <RecipientInput
                    bare
                    value={to}
                    onChange={(next) => {
                      setTo(next);
                      saveEmail({ to: toRecipientInput(next) });
                    }}
                    onSearch={searchRecipients}
                    placeholder="name@example.com"
                    aria-label="To"
                  />
                )}
              </ComposerRow>

              {ccBccShown && (
                <>
                  <ComposerRow label="Cc">
                    <RecipientInput
                      bare
                      autoFocus={ccBccFocus}
                      value={cc}
                      onChange={(next) => {
                        setCc(next);
                        saveEmail({ cc: toRecipientInput(next) });
                      }}
                      onSearch={searchRecipients}
                      aria-label="Cc"
                    />
                  </ComposerRow>

                  <ComposerRow label="Bcc">
                    <RecipientInput
                      bare
                      value={bcc}
                      onChange={(next) => {
                        setBcc(next);
                        saveEmail({ bcc: toRecipientInput(next) });
                      }}
                      onSearch={searchRecipients}
                      aria-label="Bcc"
                    />
                  </ComposerRow>
                </>
              )}

              {template.templates.canUseTemplates && (
                <ComposerRow
                  label="Template"
                  trailing={
                    !isBulkEmail && (
                      <EmailTemplatePicker
                        choices={template.templates.choices}
                        hasChoicesLoadFailed={template.templates.hasChoicesLoadFailed}
                        onRetryChoices={template.templates.retryChoices}
                        disabledReason={templatePickDisabledReason}
                        hasEditedDraft={template.hasEditedDraft}
                        onPick={template.pickTemplate}
                      />
                    )
                  }
                >
                  <EmailTemplateRowContent
                    templateUse={email?.template}
                    draftTemplate={template.templates.draftTemplate}
                    // Read-only for a bulk email: its template's name, or "No template".
                    choices={isBulkEmail ? undefined : template.templates.choices}
                    isOlderVersion={!!template.newerTemplate}
                    hasChoicesLoadFailed={!isBulkEmail && template.templates.hasChoicesLoadFailed}
                    onRetryChoices={template.templates.retryChoices}
                  />
                </ComposerRow>
              )}

              <ComposerRow label="Attachments">
                <div className="flex flex-wrap items-center gap-2">
                  {/* A workspace document is a pointer and an upload is a
                  parcel: icon, colour and byte count all tell them apart,
                  because detaching the wrong one loses work. A document from
                  another workspace always says whose it is. */}
                  {attachedArtifacts.map((attached) => (
                    <Badge key={attached.id} color={attached.origin ? 'amber' : 'violet'}>
                      <LinkIcon className="size-3" />
                      {attached.title}
                      {attached.origin && (
                        <span className="font-normal opacity-75">{attached.origin}</span>
                      )}
                      <button
                        type="button"
                        aria-label={`Remove ${attached.title}`}
                        className={clsx(
                          'ml-0.5',
                          attached.origin
                            ? 'text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200'
                            : 'text-violet-600 dark:text-violet-400 hover:text-violet-900 dark:hover:text-violet-200'
                        )}
                        onClick={() => detachArtifact(attached.id)}
                      >
                        <XMarkIcon className="size-3" />
                      </button>
                    </Badge>
                  ))}
                  {/* Dissolving one puts several attachments back on the email,
                  so it must never be mistaken for the single chip it
                  resembles. */}
                  {attachedGroups.map((group) => (
                    <Badge key={group.fileId} color="cyan">
                      <RectangleStackIcon className="size-3" />
                      <span title={groupContents(group.members)}>{group.name}</span>
                      <span className="font-normal opacity-75">{groupSummary(group)}</span>
                      {/* PDFs combined on their own have nothing to arrange:
                      to change one, separate it and combine again. */}
                      {!isPdfOnlyGroup(group.members) && (
                        <button
                          type="button"
                          aria-label={`Edit ${group.name}`}
                          className="ml-0.5 text-cyan-700 dark:text-cyan-300 hover:text-cyan-900 dark:hover:text-cyan-200"
                          onClick={() => editGroup(group)}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label={`Separate ${group.name}`}
                        className={clsx(
                          'text-cyan-700 dark:text-cyan-300 hover:text-cyan-900 dark:hover:text-cyan-200',
                          isPdfOnlyGroup(group.members) && 'ml-0.5'
                        )}
                        onClick={() => dissolveGroup(group)}
                      >
                        Separate
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${group.name}`}
                        className="text-cyan-700 dark:text-cyan-300 hover:text-cyan-900 dark:hover:text-cyan-200"
                        onClick={() => detachFile(group.fileId)}
                      >
                        <XMarkIcon className="size-3" />
                      </button>
                    </Badge>
                  ))}
                  {attachedFiles.map((file) => (
                    <Badge key={file.id} color="neutral">
                      <PaperClipIcon className="size-3" />
                      {file.name}
                      <span className="font-normal text-neutral-500 dark:text-neutral-400">
                        {formatFileSize(file.size)}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        className="ml-0.5 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
                        onClick={() => detachFile(file.id)}
                      >
                        <XMarkIcon className="size-3" />
                      </button>
                    </Badge>
                  ))}
                  {uploading.map((pending) => (
                    <Badge key={pending.key} color="neutral">
                      <Spinner size="size-3.5" color="neutral" />
                      {pending.name}
                      <span className="font-normal text-neutral-500 dark:text-neutral-400">
                        {formatFileSize(pending.size)}
                      </span>
                    </Badge>
                  ))}
                  {unresolvedArtifactIds.map((artifactId) => (
                    <Badge key={artifactId} color="red">
                      <ExclamationTriangleIcon className="size-3" />
                      Attached document no longer available
                      <button
                        type="button"
                        aria-label="Remove the attached document that is no longer available"
                        className="ml-0.5 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200"
                        onClick={() => detachArtifact(artifactId)}
                      >
                        <XMarkIcon className="size-3" />
                      </button>
                    </Badge>
                  ))}
                  {unresolvedFileIds.map((fileId) => (
                    <Badge key={fileId} color="red">
                      <ExclamationTriangleIcon className="size-3" />
                      Attachment no longer in this workspace
                      <button
                        type="button"
                        aria-label="Remove the attachment that is no longer in this workspace"
                        className="ml-0.5 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200"
                        onClick={() => detachFile(fileId)}
                      >
                        <XMarkIcon className="size-3" />
                      </button>
                    </Badge>
                  ))}
                  <Button
                    type="button"
                    plain
                    className="gap-1 px-1.5 py-0.5 text-xs text-neutral-500 dark:text-neutral-400"
                    onClick={() => openPicker(null)}
                  >
                    <PaperClipIcon className="size-3" />
                    Attach
                  </Button>
                </div>
              </ComposerRow>

              <ComposerRow
                label="Email signature"
                description={
                  signatureMissing ? (
                    <ErrorMessage>
                      This signature is no longer available to you. Choose another, or send without
                      one.
                    </ErrorMessage>
                  ) : undefined
                }
              >
                <div className="relative flex items-center">
                  <Headless.Select
                    value={signatureId}
                    onChange={(e) => {
                      setSignatureId(e.target.value);
                      saveEmail({ signatureId: e.target.value || null });
                    }}
                    aria-label="Email signature"
                    className={BARE_SELECT}
                  >
                    <option value="">No email signature</option>
                    {/* Without an option of its own the select falls back the
                      same way the From row does. */}
                    {signatureMissing && (
                      <option value={signatureId}>Email signature no longer available</option>
                    )}
                    {signatures.map((signature) => (
                      <option key={signature.id} value={signature.id}>
                        {signature.name}
                        {signature.isDefault ? ' (default)' : ''}
                      </option>
                    ))}
                  </Headless.Select>
                  <ChevronDownIcon
                    className="pointer-events-none absolute right-0 size-4 text-neutral-400 dark:text-neutral-500"
                    aria-hidden="true"
                  />
                </div>
              </ComposerRow>

              <EmailTemplateNotices
                template={template}
                redraftDisabledReason={templatePickDisabledReason}
                isBulkEmail={isBulkEmail}
              />
              {/* No visible Message label: the last hairline of the ledger is
              the boundary. The editor's stylesheet is written for documents, so
              the composer sets prose metrics, and the side padding the bare chrome
              leaves out, for the one place a document is a message. `flex-1` in
              a min-h-full column gives a two-line draft a full page before
              anything scrolls. */}
              <Field
                className={clsx(
                  'flex flex-1 flex-col',
                  '[&_.ProseMirror]:px-4 [&_.tiptap]:text-[0.9375rem] [&_.tiptap]:leading-7',
                  '[&_.tiptap_p]:text-[0.9375rem] [&_.tiptap_p]:leading-7 [&_.tiptap_p+p]:mt-3',
                  template.isDraftedFromTemplate &&
                    templateHighlightClassName(template.isHighlighting)
                )}
              >
                <Label className="sr-only" data-slot="none">
                  Message
                </Label>
                <HtmlDocumentEditor
                  key={editorKey}
                  chrome="bare"
                  showImageButton={false}
                  extensions={
                    template.isDraftedFromTemplate ? emailDraftMarkerExtensions : undefined
                  }
                  onEditorReady={template.setBodyEditor}
                  content={email?.bodyHtml ?? ''}
                  editable={
                    workspace.state !== CaraWorkspaceState.Working && !locked && !bodyHydrating
                  }
                  onUpdate={(bodyHtml) => {
                    bodyHtmlRef.current = bodyHtml;
                    template.recordBodyEdit(bodyHtml);
                    // The size check rides the save rather than the paste: the
                    // editor exposes no paste hook. An over-limit body is kept
                    // out of the artifact — saving it every 300ms while the
                    // sender hunts for the image would ship those megabytes
                    // over and over.
                    const refusal = bodyRefusal({
                      size: bodyHtml.length,
                      limitBytes: composer.bodyLimitBytes
                    });
                    setBodyError(refusal ?? null);
                    if (refusal) {
                      return;
                    }
                    saveEmail({ bodyHtml });
                  }}
                  workspaceId={workspace.id}
                  artifactId={artifact.id}
                  artifactVersion={artifact.version}
                />
              </Field>

              {selectedSignature && (
                <SignatureFooter
                  name={selectedSignature.name}
                  bodyHtml={selectedSignature.bodyHtml}
                  appendedOnSend={mailboxEnabled}
                />
              )}
            </Fieldset>
          </div>
        </div>
      </div>
    </ArtifactFocusShell>
  );
};
